import type { CardString } from './general_definitions';
import { Rank, Suit } from './general_definitions';
import { NameList } from './names';
import { getRandomValue } from './random';
import { getPlayerHandName } from './utils';

const Suits = ['S', 'C', 'D', 'H'];

export interface CardObject {
	rank: Rank;
	suit: Suit;
	pile: string;
	order?: number;
	isSelected?: boolean;
}

export function createDeck(): CardObject[] {
	const deck = [];

	for (const suit of Suits) {
		for (let x = 1; x <= 13; ++x) {
			let rank;

			switch (x) {
				case 1:
					rank = 'A';
					break;
				case 11:
					rank = 'J';
					break;
				case 12:
					rank = 'Q';
					break;
				case 13:
					rank = 'K';
					break;
				default:
					rank = x;
					break;
			}

			deck.push(getCardObjectFromString(`${rank}${suit}` as CardString));
		}
	}

	return deck;
}

export function getRankByValue(value: number): Rank {
	switch (value) {
		case 2:
			return Rank.Two;
		case 3:
			return Rank.Three;
		case 4:
			return Rank.Four;
		case 5:
			return Rank.Five;
		case 6:
			return Rank.Six;
		case 7:
			return Rank.Seven;
		case 8:
			return Rank.Eight;
		case 9:
			return Rank.Nine;
		case 10:
			return Rank.Ten;
		case 11:
			return Rank.Jack;
		case 12:
			return Rank.Queen;
		case 13:
			return Rank.King;
		case 14:
			return Rank.Ace;
		default:
			throw new Error('Invalid Rank value ' + value);
	}
}

export function getRankValue(rank: Rank): number {
	switch (rank) {
		case Rank.Ace:
			return 14;
		case Rank.King:
			return 13;
		case Rank.Queen:
			return 12;
		case Rank.Jack:
			return 11;
		default:
			return parseInt(rank);
	}
}

export function compareRank(l: Rank, r: Rank): number {
	return getRankValue(l) - getRankValue(r);
}

export function compareSuit(l: Suit, r: Suit): number {
	return getSuitValue(l) - getSuitValue(r);
}

export function getSuitValue(suit: Suit): number {
	switch (suit) {
		case Suit.Clubs:
			return 0;
		case Suit.Diamonds:
			return 1;
		case Suit.Spades:
			return 2;
		case Suit.Hearts:
			return 3;
	}
}

export function getSuit(card: CardString): Suit {
	const suit = card.charAt(card.length - 1).toUpperCase();

	switch (suit) {
		case 'H':
			return Suit.Hearts;
		case 'D':
			return Suit.Diamonds;
		case 'S':
			return Suit.Spades;
		case 'C':
			return Suit.Clubs;
	}

	throw new Error(`Invalid suit for card: '${suit}'`);
}

export function getRank(card: CardString): Rank {
	const rank = card.substring(0, card.length - 1);
	return (rank as Rank);
}

//This bit of code is to assert that an object is of a type
//From https://github.com/microsoft/TypeScript/issues/10421#issuecomment-518806979
export function assumeType<T>(_x: unknown): asserts _x is T {
	return; // ¯\_(ツ)_/¯
}

export function deepCopy<F>(inObject: Record<string, unknown>): F {

	if (typeof inObject !== 'object' || inObject === null) {
		return inObject as F; // Return the value if inObject is not an object
	}

	// Create an array or object to hold the values
	const outObject = Array.isArray(inObject) ? [] : {};

	for (const key of Object.keys(inObject)) {
		const value = (inObject as never)[key];

		// Recursively (deep) copy for nested objects, including arrays
		(outObject as never)[key] = deepCopy(value);
	}

	return outObject as F;
}

export function shuffle(inArray: unknown[]): void {
	if (!Array.isArray(inArray)) {
		return inArray;
	}

	for (let i = inArray.length - 1; i > 0; --i) {
		const j = Math.floor(getRandomValue() * i);
		const temp = inArray[i];
		inArray[i] = inArray[j];
		inArray[j] = temp;
	}
}

export function decodeMovePile(pile: string): { player: number, place: string } | null {
	const ind = pile.search(/\d+/);

	if (ind > -1) {
		return {
			player: parseInt(pile.substring(ind)),
			place: pile.substring(0, ind),
		};
	}

	return null;
}

export function getCardsInHand(cards: CardObject[], position: number): CardObject[] {
	return getCardsInPile(cards, getPlayerHandName(position));
}

export function getCardsInPile(cards: CardObject[], pile: string): CardObject[] {
	return cards.filter((c) => c.pile === pile);
}

export function compareCardTo(l: CardObject, r: CardObject): number {
	if (r.suit === l.suit) {
		return compareRank(l.rank, r.rank);
	}

	return compareSuit(l.suit, r.suit);
}

export function sortCards(cards: CardObject[]): CardObject[] {
	const newArray = Array.from(cards);
	newArray.sort(compareCardTo);
	return newArray;
}

export function getRandomItem<A>(items: A[]): A {
	return items[Math.floor(getRandomValue() * items.length)];
}

export function getListOfNames(): string[] {
	const shuffled = Array.from(NameList);
	shuffle(shuffled);
	return shuffled;
}

export function getCardString(card: CardObject): CardString {
	const { rank, suit } = card;
	return `${rank}${suit}` as CardString;
}

export function getCardObjectFromString(cardString: CardString, order = 0, pile = ''): CardObject {
	const rank = getRank(cardString);
	const suit = getSuit(cardString);
	return {
		rank,
		suit,
		order,
		pile,
	};
}
